import {
  CheckCircle,
  CloudSync,
  DevicesOther,
  Groups,
  HomeWork,
  Person,
  RadioButtonUnchecked,
  RocketLaunch,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  LinearProgress,
  Typography,
} from "@mui/material";
import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDataProvider, useGetList, useRedirect, useTranslate } from "react-admin";

import { DeviceEnrollmentWizard } from "../components/onboarding/DeviceEnrollmentWizard";
import {
  type ExistingRecord,
  type ExistingRecordsByResource,
  getPatientLocationId,
  getPractitionerLocationIds,
  type ManualLocationChoice,
  type ManualSetupSubmission,
} from "../components/onboarding/ManualSetupEditors";
import { OnboardingWizard } from "../components/onboarding/OnboardingWizard";
import { ResourceName } from "../constants/resources";
import { type CompleteOnboardingInput, LocationTypeEnum } from "../generated/graphql";
import { useOrganizationContext } from "../hooks/useOrganizationContext";
import { captureException } from "../lib/errorHandling";
import type { CustomDataProvider } from "../providers/dataProvider";
import { borderRadius, color, shadows, space } from "../theme/designTokens";
import type { LocationRecord } from "../types/location";
import type { PatientRecord } from "../types/patient";
import type { PractitionerRecord } from "../types/practitioner";

// Manual onboarding shows what already exists; organizations in setup are small.
const EXISTING_RECORDS_PAGE_SIZE = 1000;

const joinDetails = (details: Array<string | null | undefined>) => details.filter(Boolean).join(" · ") || undefined;

const getLocationPath = (locationId: string | null | undefined, choices: ManualLocationChoice[]) => {
  const location = choices.find(choice => choice.id === locationId);
  if (!location) {
    return undefined;
  }
  const parent = choices.find(choice => choice.id === location.partOfId);
  return parent && parent.type !== LocationTypeEnum.Organization ? `${parent.name} · ${location.name}` : location.name;
};

export const getExistingRecords = (
  locationChoices: ManualLocationChoice[],
  practitioners: PractitionerRecord[],
  patients: PatientRecord[]
): ExistingRecordsByResource => ({
  location: locationChoices
    .filter(choice => choice.type === LocationTypeEnum.Site)
    .map<ExistingRecord>(site => ({
      id: site.id,
      primary: site.name,
      secondary: joinDetails(locationChoices.filter(choice => choice.partOfId === site.id).map(ward => ward.name)),
    })),
  patient: patients.map<ExistingRecord>(patient => ({
    id: patient.id,
    primary: patient.name,
    secondary: joinDetails([patient.patientNumber, getLocationPath(patient.locationId, locationChoices)]),
  })),
  practitioner: practitioners.map<ExistingRecord>(practitioner => ({
    id: practitioner.id,
    primary: practitioner.name,
    secondary: joinDetails([
      practitioner.employeeNumber,
      practitioner.locations
        .filter(location => location.type !== LocationTypeEnum.Organization)
        .map(location => location.name)
        .join(", "),
    ]),
  })),
});

export type OnboardingReadinessInput = {
  locationCount: number;
  practitionerCount: number;
  patientCount: number;
  deviceCount: number;
  integrationConfigured: boolean;
};

export type OnboardingReadiness = OnboardingReadinessInput & {
  coreCompletedCount: number;
  coreStepCount: number;
  coreComplete: boolean;
  progressPercent: number;
};

export const getOnboardingReadiness = (input: OnboardingReadinessInput): OnboardingReadiness => {
  const coreSteps = [input.locationCount > 0, input.practitionerCount > 0, input.patientCount > 0];
  const coreCompletedCount = coreSteps.filter(Boolean).length;
  const coreStepCount = coreSteps.length;

  return {
    ...input,
    coreCompletedCount,
    coreStepCount,
    coreComplete: coreCompletedCount === coreStepCount,
    progressPercent: (coreCompletedCount / coreStepCount) * 100,
  };
};

type IntegrationDataProvider = Pick<CustomDataProvider, "completeOnboarding" | "getIntegrationSettings">;

type ReadinessCardProps = {
  complete: boolean;
  count?: number;
  icon: ReactNode;
  label: string;
  status: string;
};

const ReadinessCard = ({ complete, count, icon, label, status }: ReadinessCardProps) => (
  <Card
    variant="outlined"
    sx={{
      borderColor: complete ? color.plannedBorder : color.panelBorder,
      borderRadius: borderRadius.md,
      boxShadow: shadows.none,
      height: "100%",
    }}
  >
    <CardContent sx={{ alignItems: "center", display: "flex", gap: space.xs }}>
      <Box sx={{ color: complete ? color.success : color.secondary40, display: "flex" }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography fontWeight="medium">{label}</Typography>
        <Typography color="text.secondary" variant="body2">
          {count === undefined ? status : `${count} · ${status}`}
        </Typography>
      </Box>
      <Box sx={{ color: complete ? color.success : color.secondary40, display: "flex", ml: "auto" }}>
        {complete ? <CheckCircle /> : <RadioButtonUnchecked />}
      </Box>
    </CardContent>
  </Card>
);

export const OnboardingPage = () => {
  const translate = useTranslate();
  const redirect = useRedirect();
  const dataProvider = useDataProvider() as unknown as IntegrationDataProvider;
  const { organizationId, isLoading: isOrganizationLoading } = useOrganizationContext();
  const [wizardOpen, setWizardOpen] = useState(false);
  const [deviceWizardOpen, setDeviceWizardOpen] = useState(false);
  const [integrationConfigured, setIntegrationConfigured] = useState(false);
  const [isIntegrationLoading, setIsIntegrationLoading] = useState(true);
  const [integrationError, setIntegrationError] = useState(false);
  const integrationRequest = useRef(0);

  const listParams = useMemo(
    () => ({
      filter: { organizationId },
      pagination: { page: 1, perPage: 1 },
    }),
    [organizationId]
  );
  const queryOptions = { enabled: !!organizationId };

  const locations = useGetList(
    ResourceName.Location,
    {
      ...listParams,
      filter: { ...listParams.filter, type: LocationTypeEnum.Site },
    },
    queryOptions
  );
  const assignableLocations = useGetList<LocationRecord>(
    ResourceName.Location,
    {
      filter: { organizationId },
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "name", order: "ASC" },
    },
    queryOptions
  );
  const recordListParams = useMemo(
    () => ({
      filter: { organizationId },
      pagination: { page: 1, perPage: EXISTING_RECORDS_PAGE_SIZE },
    }),
    [organizationId]
  );
  const practitioners = useGetList<PractitionerRecord>(ResourceName.Practitioner, recordListParams, queryOptions);
  const patients = useGetList<PatientRecord>(ResourceName.Patient, recordListParams, queryOptions);
  const devices = useGetList(ResourceName.Device, listParams, queryOptions);

  const loadIntegrationSettings = useCallback(
    async (showLoading = true) => {
      const requestId = ++integrationRequest.current;
      if (!organizationId) {
        setIntegrationConfigured(false);
        setIntegrationError(false);
        setIsIntegrationLoading(false);
        return;
      }
      if (showLoading) {
        setIsIntegrationLoading(true);
      }
      setIntegrationError(false);
      try {
        const settings = await dataProvider.getIntegrationSettings(organizationId);
        if (requestId === integrationRequest.current) setIntegrationConfigured(!!settings);
      } catch (error) {
        if (requestId !== integrationRequest.current) return;
        setIntegrationError(true);
        captureException(error, "onboarding: load integration settings");
      } finally {
        if (requestId === integrationRequest.current) {
          setIsIntegrationLoading(false);
        }
      }
    },
    [dataProvider, organizationId]
  );

  useEffect(() => {
    void loadIntegrationSettings();
    return () => {
      integrationRequest.current += 1;
    };
  }, [loadIntegrationSettings]);

  const readiness = getOnboardingReadiness({
    locationCount: locations.total ?? 0,
    practitionerCount: practitioners.total ?? 0,
    patientCount: patients.total ?? 0,
    deviceCount: devices.total ?? 0,
    integrationConfigured,
  });

  const locationChoices = useMemo<ManualLocationChoice[]>(
    () =>
      (assignableLocations.data ?? [])
        .filter(
          (location): location is LocationRecord & { type: LocationTypeEnum } =>
            location.type !== null && location.type !== undefined
        )
        .map(location => ({
          id: location.id,
          name: location.name,
          partOfId: location.partOfId,
          type: location.type,
        })),
    [assignableLocations.data]
  );
  const existingRecords = useMemo(
    () => getExistingRecords(locationChoices, practitioners.data ?? [], patients.data ?? []),
    [locationChoices, patients.data, practitioners.data]
  );

  const hasListError = !!(
    locations.error ||
    assignableLocations.error ||
    practitioners.error ||
    patients.error ||
    devices.error
  );
  const isLoading =
    isOrganizationLoading ||
    locations.isPending ||
    assignableLocations.isPending ||
    practitioners.isPending ||
    patients.isPending ||
    devices.isPending ||
    isIntegrationLoading;

  const refreshProgress = useCallback(async () => {
    const results = await Promise.allSettled([
      locations.refetch(),
      assignableLocations.refetch(),
      practitioners.refetch(),
      patients.refetch(),
      devices.refetch(),
      loadIntegrationSettings(false),
    ]);
    const failure = results.find((result): result is PromiseRejectedResult => result.status === "rejected");
    if (failure) {
      captureException(failure.reason, "onboarding: refresh readiness");
    }
  }, [assignableLocations, devices, loadIntegrationSettings, locations, patients, practitioners]);

  const submitManualSetup = useCallback(
    async ({
      locations: locationDrafts,
      patients: patientDrafts,
      practitioners: practitionerDrafts,
    }: ManualSetupSubmission) => {
      if (!organizationId) {
        throw new Error("Cannot complete onboarding without an organization");
      }

      const onboardingInput: CompleteOnboardingInput = {
        locations: locationDrafts.flatMap(location => [
          {
            clientId: location.id,
            name: location.name.trim(),
            type: LocationTypeEnum.Site,
          },
          ...location.wards.map(ward => ({
            clientId: ward.id,
            name: ward.name.trim(),
            parentReference: location.id,
            type: LocationTypeEnum.Ward,
          })),
        ]),
        organizationId,
        patients: patientDrafts.map(patient => {
          if (!patient.gender || !patient.salutation) {
            throw new Error("Cannot submit an incomplete patient row");
          }
          return {
            birthDate: patient.birthDate,
            firstName: patient.firstName.trim(),
            gender: patient.gender,
            lastName: patient.lastName.trim(),
            locationReference: getPatientLocationId(patient),
            patientNumber: patient.patientNumber.trim(),
            salutation: patient.salutation,
          };
        }),
        practitioners: practitionerDrafts.map(practitioner => {
          if (!practitioner.salutation) {
            throw new Error("Cannot submit an incomplete practitioner row");
          }
          return {
            email: practitioner.email.trim() || undefined,
            firstName: practitioner.firstName.trim(),
            lastName: practitioner.lastName.trim(),
            locationReferences: getPractitionerLocationIds(practitioner),
            salutation: practitioner.salutation,
            status: practitioner.status,
            workerNumber: practitioner.workerNumber.trim(),
          };
        }),
      };

      await dataProvider.completeOnboarding(onboardingInput);
      await refreshProgress();
    },
    [dataProvider, organizationId, refreshProgress]
  );

  const closeWizard = () => {
    setWizardOpen(false);
    void refreshProgress();
  };

  const openDeviceEnrollment = () => {
    setWizardOpen(false);
    setDeviceWizardOpen(true);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: space.xxl }}>
        <CircularProgress aria-label={translate("onboarding.loading")} />
      </Box>
    );
  }

  if (!organizationId) {
    return <Alert severity="error">{translate("onboarding.noOrganization")}</Alert>;
  }

  const readinessCards: ReadinessCardProps[] = [
    {
      complete: readiness.locationCount > 0,
      count: readiness.locationCount,
      icon: <HomeWork />,
      label: translate("onboarding.readiness.locations"),
      status: translate(readiness.locationCount > 0 ? "onboarding.status.complete" : "onboarding.status.missing"),
    },
    {
      complete: readiness.practitionerCount > 0,
      count: readiness.practitionerCount,
      icon: <Groups />,
      label: translate("onboarding.readiness.practitioners"),
      status: translate(readiness.practitionerCount > 0 ? "onboarding.status.complete" : "onboarding.status.missing"),
    },
    {
      complete: readiness.patientCount > 0,
      count: readiness.patientCount,
      icon: <Person />,
      label: translate("onboarding.readiness.patients"),
      status: translate(readiness.patientCount > 0 ? "onboarding.status.complete" : "onboarding.status.missing"),
    },
    {
      complete: readiness.integrationConfigured,
      icon: <CloudSync />,
      label: translate("onboarding.readiness.integration"),
      status: translate(
        integrationError
          ? "onboarding.status.unavailable"
          : readiness.integrationConfigured
            ? "onboarding.status.configured"
            : "onboarding.status.optional"
      ),
    },
    {
      complete: readiness.deviceCount > 0,
      count: readiness.deviceCount,
      icon: <DevicesOther />,
      label: translate("onboarding.readiness.devices"),
      status: translate(readiness.deviceCount > 0 ? "onboarding.status.registered" : "onboarding.status.notRegistered"),
    },
  ];

  return (
    <Box data-testid="onboarding-page" sx={{ display: "flex", flexDirection: "column", gap: space.sm, maxWidth: 1100 }}>
      <Box sx={{ alignItems: "flex-start", display: "flex", flexWrap: "wrap", gap: space.xs }}>
        <Box>
          <Typography color="text.secondary">{translate("onboarding.subtitle")}</Typography>
        </Box>
        <Button
          onClick={() => setWizardOpen(true)}
          startIcon={<RocketLaunch />}
          sx={{ ml: "auto" }}
          variant="contained"
        >
          {translate("onboarding.openWizard")}
        </Button>
      </Box>

      {hasListError && <Alert severity="error">{translate("onboarding.loadError")}</Alert>}

      <Card
        variant="outlined"
        sx={{ borderColor: color.panelBorder, borderRadius: borderRadius.md, boxShadow: shadows.card }}
      >
        <CardContent sx={{ display: "flex", flexDirection: "column", gap: space.xs }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: space.xs, justifyContent: "space-between" }}>
            <Box>
              <Typography variant="h6">{translate("onboarding.progress.title")}</Typography>
              <Typography color="text.secondary" variant="body2">
                {translate("onboarding.progress.steps", {
                  completed: readiness.coreCompletedCount,
                  total: readiness.coreStepCount,
                })}
              </Typography>
            </Box>
            <Chip
              color={readiness.coreComplete ? "success" : "default"}
              label={translate(readiness.coreComplete ? "onboarding.status.ready" : "onboarding.status.inProgress")}
            />
          </Box>
          <LinearProgress variant="determinate" value={readiness.progressPercent} />
        </CardContent>
      </Card>

      <Box
        sx={{
          display: "grid",
          gap: space.xs,
          gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
        }}
      >
        {readinessCards.map(card => (
          <ReadinessCard key={card.label} {...card} />
        ))}
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: space.xs }}>
        <Button onClick={() => setWizardOpen(true)} startIcon={<RocketLaunch />} variant="contained">
          {translate("onboarding.openWizard")}
        </Button>
        <Button onClick={openDeviceEnrollment} startIcon={<DevicesOther />} variant="outlined">
          {translate("onboarding.openDeviceGuide")}
        </Button>
      </Box>

      <OnboardingWizard
        counts={{
          locationCount: readiness.locationCount,
          patientCount: readiness.patientCount,
          practitionerCount: readiness.practitionerCount,
        }}
        existingRecords={existingRecords}
        locationChoices={locationChoices}
        onClose={closeWizard}
        onOpenDeviceEnrollment={openDeviceEnrollment}
        onSubmitManualSetup={submitManualSetup}
        open={wizardOpen}
      />
      <DeviceEnrollmentWizard
        deviceCount={readiness.deviceCount}
        onClose={() => setDeviceWizardOpen(false)}
        onShowDevices={() => redirect(`/${ResourceName.Device}`)}
        open={deviceWizardOpen}
      />
    </Box>
  );
};
